import { DEFAULT_DATA, SUPPLEMENT, type StreakData } from "./types";
import { loadSupplement, saveSupplement } from "./local-store";
import { api, email, userId } from "./api-client";
import { withRetry } from "./retry";

type StreakStateResponse = {
  checkins: string[];
  meta: {
    longestStreak: number;
    isFirstVisit: boolean;
    firstCheckinDate: string | null;
  };
};

function fromRemote(remote: StreakStateResponse): StreakData {
  return {
    checkins: remote.checkins,
    longestStreak: remote.meta.longestStreak,
    isFirstVisit: remote.meta.isFirstVisit,
    firstCheckinDate: remote.meta.firstCheckinDate,
  };
}

function hasLocalData(d: StreakData): boolean {
  return d.checkins.length > 0 || d.longestStreak > 0 || !d.isFirstVisit;
}

/**
 * Loads the current streak state.
 * - No userId → local-only, returns localStorage immediately.
 * - With userId → upserts user, migrates local data if new, then pulls remote.
 *   On network failure after retries, falls back to local.
 */
export async function loadState(): Promise<StreakData> {
  const local = loadSupplement();
  if (!api || !userId) return local;

  const a = api; // narrow for closures
  const uid = userId;

  try {
    const userRes = await withRetry(() =>
      a.api.streak.user.$post({
        json: { userId: uid, email: email ?? undefined },
      }),
    );
    if (!userRes.ok) throw new Error(`POST /user failed: ${userRes.status}`);
    const { isNew } = (await userRes.json()) as { isNew: boolean };

    if (isNew && hasLocalData(local)) {
      await withRetry(() =>
        a.api.streak.migrate.$post({
          json: { userId: uid, supplement: SUPPLEMENT, data: local },
        }),
      );
    }

    const stateRes = await withRetry(() =>
      a.api.streak[":userId"][":supplement"].$get({
        param: { userId: uid, supplement: SUPPLEMENT },
      }),
    );
    if (!stateRes.ok) throw new Error(`GET state failed: ${stateRes.status}`);
    const remote = (await stateRes.json()) as StreakStateResponse;
    const merged = fromRemote(remote);
    saveSupplement(merged);
    return merged;
  } catch (err) {
    console.warn("[streak-tracker] loadState fell back to local:", err);
    return local;
  }
}

/**
 * Write-through: localStorage first, then best-effort API call with 3x retry.
 * Caller has already updated React state; errors are swallowed (local is authoritative).
 */
export async function recordCheckin(
  next: StreakData,
  date: string,
): Promise<void> {
  saveSupplement(next);
  if (!api || !userId) return;
  const a = api;
  const uid = userId;
  try {
    await withRetry(() =>
      a.api.streak.checkin.$post({
        json: { userId: uid, supplement: SUPPLEMENT, date },
      }),
    );
  } catch (err) {
    console.warn("[streak-tracker] checkin sync failed after retries:", err);
  }
}

export async function markStartedTracking(next: StreakData): Promise<void> {
  saveSupplement(next);
  if (!api || !userId) return;
  const a = api;
  const uid = userId;
  try {
    await withRetry(() =>
      a.api.streak.meta.$patch({
        json: {
          userId: uid,
          supplement: SUPPLEMENT,
          isFirstVisit: false,
        },
      }),
    );
  } catch (err) {
    console.warn("[streak-tracker] markStartedTracking sync failed:", err);
  }
}

export async function resetAll(
  _next: StreakData = DEFAULT_DATA,
): Promise<void> {
  saveSupplement(DEFAULT_DATA);
  if (!api || !userId) return;
  const a = api;
  const uid = userId;
  try {
    await withRetry(() =>
      a.api.streak.meta.$patch({
        json: {
          userId: uid,
          supplement: SUPPLEMENT,
          longestStreak: 0,
          isFirstVisit: true,
          firstCheckinDate: null,
        },
      }),
    );
  } catch (err) {
    console.warn("[streak-tracker] resetAll sync failed:", err);
  }
}
